/**
 * The MCP tab's rail (left): each service with its MCP servers (path, transport, declared or found in the code; a
 * stopped service with Start), then, once connected, what the server offers: Tools, Resources, Resource templates and
 * Prompts, then History (this session's calls, with their outcome).
 */
import React from 'react';
import PropTypes from 'prop-types';
import ServiceStartButton from '../sandbox/ServiceStartButton';
import { SOURCE_LABELS, TRANSPORT_LABELS, serverKey } from '../../utils/mcpTesterModel';

const KINDS = [
  ['tools', 'Tools', (item) => item.name, (item) => item.description],
  ['resources', 'Resources', (item) => item.name || item.uri, (item) => item.uri],
  ['resourceTemplates', 'Resource templates', (item) => item.name || item.uriTemplate, (item) => item.uriTemplate],
  ['prompts', 'Prompts', (item) => item.name, (item) => item.description],
];

export const itemKey = (kind, item) => `${kind}:${item.name || item.uri || item.uriTemplate}`;

export default function McpRail({
  services, selected, onSelectServer, connection, selectedItem, onSelectItem, history, onPickHistory, onClearHistory, projectId, canStart, onStarted,
}) {
  return (
    <aside aria-label="MCP servers" className="api-rail mcp-rail">
      {services.map((service) => (
        <div aria-label={service.name} className="api-group" key={service.service} role="group">
          <div className="api-group-label"><span>{service.name}</span><span className="api-group-source">{service.servers.length} server{service.servers.length === 1 ? '' : 's'}</span></div>
          {!service.running && (
            <div className="api-group-note api-group-stopped">
              <span>{service.state === 'building' ? 'Starting…' : 'Not running: start it to connect'}</span>
              {canStart && service.state !== 'building' && <ServiceStartButton onStarted={onStarted} projectId={projectId} serviceName={service.service} />}
            </div>
          )}
          {service.servers.map((server) => {
            const key = serverKey(service.service, server);
            const on = selected === key;
            return (
              <button
                aria-current={on ? 'true' : undefined}
                className={`api-ep${on ? ' is-on' : ''}`}
                data-testid="mcp-server"
                disabled={server.transport === 'stdio'}
                key={key}
                onClick={() => onSelectServer(service, server)}
                title={server.note || (server.file ? `${server.file}${server.line ? `:${server.line}` : ''}` : undefined)}
                type="button"
              >
                <span className="api-ep-path">{server.path || '(stdio)'}</span>
                <span className="api-ep-tag">{TRANSPORT_LABELS[server.transport] || server.transport}</span>
                <span className="api-ep-tag" title={server.source === 'service.json' ? 'Declared in .flow/service.json' : `Found in ${server.file}`}>{SOURCE_LABELS[server.source] || server.source}</span>
              </button>
            );
          })}
          {service.servers.filter((s) => s.note).map((s) => <p className="api-group-note" key={`${s.path}-note`}>{s.note}</p>)}
        </div>
      ))}
      {connection && KINDS.map(([kind, label, nameOf, hintOf]) => {
        const items = connection[kind] || [];
        if (!items.length && !connection.errors?.[kind]) { return null; }
        return (
          <div aria-label={label} className="api-group" key={kind} role="group">
            <div className="api-group-label"><span>{label}</span><span className="api-group-source">{items.length}</span></div>
            {connection.errors?.[kind] && <p className="api-group-note data-error-text">{connection.errors[kind]}</p>}
            {items.map((item) => {
              const key = itemKey(kind, item);
              return (
                <button
                  aria-current={selectedItem === key ? 'true' : undefined}
                  className={`api-ep mcp-item${selectedItem === key ? ' is-on' : ''}`}
                  data-testid={`mcp-item-${kind}`}
                  key={key}
                  onClick={() => onSelectItem(kind, item)}
                  title={hintOf(item) || undefined}
                  type="button"
                >
                  <span className="api-ep-path">{nameOf(item)}</span>
                </button>
              );
            })}
          </div>
        );
      })}
      <div aria-label="History" className="api-group" role="group">
        <div className="api-group-label">
          <span>History</span>
          {history.length > 0 && <button className="data-link api-clear" onClick={onClearHistory} type="button">Clear</button>}
        </div>
        {!history.length && <p className="api-group-note">This session&apos;s calls show here.</p>}
        {history.map((entry) => (
          <button className="api-ep" data-testid="mcp-history" key={entry.id} onClick={() => onPickHistory(entry)} title={`${entry.service.replace(/^services\//, '')} ${entry.path} · ${new Date(entry.at).toLocaleTimeString()}`} type="button">
            <span className="api-ep-path">{entry.label}</span>
            <span className={`data-pill ${entry.ok ? 'is-ok' : 'is-bad'}`}>{entry.ok ? 'ok' : entry.isError ? 'isError' : 'error'}</span>
          </button>
        ))}
      </div>
      <p className="data-footnote">Servers come from each service&apos;s code, or its .flow/service.json &quot;mcp&quot;. Calls go to the running preview, inside your sandbox.</p>
    </aside>
  );
}

McpRail.propTypes = {
  canStart: PropTypes.bool,
  connection: PropTypes.object,
  history: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClearHistory: PropTypes.func.isRequired,
  onPickHistory: PropTypes.func.isRequired,
  onSelectItem: PropTypes.func.isRequired,
  onSelectServer: PropTypes.func.isRequired,
  onStarted: PropTypes.func,
  projectId: PropTypes.string.isRequired,
  selected: PropTypes.string,
  selectedItem: PropTypes.string,
  services: PropTypes.arrayOf(PropTypes.object).isRequired,
};
